import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LOGO_SRC from "../assets/logo";

const LANGS = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "ar", label: "العربية", flag: "🇲🇦" },
];

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Close lang dropdown when clicking outside
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".lang-dropdown-wrap")) setLangOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (path) => { navigate(path); window.scrollTo(0, 0); };
  const changeLang = (code) => { i18n.changeLanguage(code); setLangOpen(false); };

  const nav = [
    { path: "/",             label: t("nav.home") },
    { path: "/packages",     label: t("nav.packages") },
    { path: "/destinations", label: t("nav.destinations") },
    { path: "/about",        label: t("nav.about") },
    { path: "/contact",      label: t("nav.contact") },
  ];

  const currentLang = LANGS.find(l => l.code === i18n.language) || LANGS[0];
  const LOGO_HEIGHT = 145;

  return (
    <>
      <style>{`
        .navbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          padding: 0 40px; transition: all 0.4s ease;
          display: flex; align-items: center; justify-content: space-between;
          height: 92px;
        }
        .navbar.scrolled {
          background: rgba(13,27,42,0.97); backdrop-filter: blur(12px);
          box-shadow: 0 4px 30px rgba(0,0,0,0.3); height: 76px;
        }
        .navbar.transparent { background: transparent; }
        .navbar-logo { display:flex; align-items:center; cursor:pointer; }
        .navbar-logo img { height: ${LOGO_HEIGHT}px; width: auto; object-fit: contain; }

        .nav-links { display:flex; gap:4px; align-items:center; }
        .nav-link {
          color: rgba(255,255,255,0.85); font-size:14px; font-weight:500;
          padding:8px 14px; border-radius:4px; cursor:pointer;
          transition:all 0.2s; font-family:inherit; border:none; background:transparent;
        }
        .nav-link:hover, .nav-link.active { color:var(--gold-light); }
        .nav-cta {
          background: linear-gradient(135deg, var(--gold), var(--gold-dark));
          color:white; padding:10px 20px; border-radius:4px; font-size:14px;
          font-weight:600; cursor:pointer; border:none; font-family:inherit;
          transition:all 0.2s; margin-inline-start:4px;
        }
        .nav-cta:hover { opacity:0.9; transform:translateY(-1px); }

        /* ── Language Switcher ── */
        .lang-dropdown-wrap {
          position: relative; margin-inline-start: 10px;
        }
        .lang-trigger {
          display: flex; align-items: center; gap: 6px;
          padding: 6px 12px; border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.25);
          background: rgba(255,255,255,0.08);
          color: white; cursor: pointer; font-family: inherit;
          font-size: 13px; font-weight: 500;
          transition: all 0.2s; user-select: none;
        }
        .lang-trigger:hover {
          background: rgba(255,255,255,0.15);
          border-color: rgba(201,168,76,0.5);
        }
        .lang-trigger .flag { font-size: 16px; line-height: 1; }
        .lang-trigger .chevron {
          font-size: 9px; opacity: 0.7;
          transition: transform 0.2s;
          display: inline-block;
        }
        .lang-trigger .chevron.open { transform: rotate(180deg); }

        .lang-dropdown {
          position: absolute; top: calc(100% + 8px);
          inset-inline-end: 0;
          background: rgba(13,27,42,0.97); backdrop-filter: blur(12px);
          border: 1px solid rgba(201,168,76,0.2);
          border-radius: 10px; overflow: hidden;
          box-shadow: 0 12px 40px rgba(0,0,0,0.35);
          min-width: 150px;
          animation: fadeUp 0.15s ease forwards;
        }
        .lang-option {
          display: flex; align-items: center; gap: 10px;
          padding: 11px 16px; cursor: pointer;
          color: rgba(255,255,255,0.8); font-size: 13px; font-weight: 500;
          transition: all 0.15s; border: none; background: transparent;
          width: 100%; font-family: inherit; text-align: start;
        }
        .lang-option:hover { background: rgba(201,168,76,0.12); color: white; }
        .lang-option.active { color: var(--gold-light); }
        .lang-option .opt-flag { font-size: 18px; }
        .lang-option .opt-label { flex: 1; }
        .lang-option .opt-check { color: var(--gold); font-size: 12px; }

        /* ── Hamburger ── */
        .hamburger { display:none; flex-direction:column; gap:5px; cursor:pointer; padding:4px; background:none; border:none; }
        .hamburger span { width:24px; height:2px; background:white; border-radius:2px; transition:all 0.3s; display:block; }
        @media(max-width:900px) {
          .navbar { padding:0 20px; }
          .navbar-logo img { height: 76px; }
          .nav-links.desktop { display:none; }
          .hamburger { display:flex; }
        }

        /* ── Mobile menu ── */
        .mobile-menu {
          position:fixed; top:64px; left:0; right:0; bottom:0; z-index:999;
          background:rgba(13,27,42,0.98);
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          gap:6px; animation: fadeIn 0.2s ease;
        }
        .mobile-nav-link {
          color:white; font-size:22px; font-family:var(--font-heading);
          padding:12px 40px; cursor:pointer; border-radius:4px;
          transition:color 0.2s; border:none; background:transparent;
          text-align:center; width:100%;
        }
        .mobile-nav-link:hover { color:var(--gold-light); }

        /* Mobile lang switcher */
        .mobile-lang-row {
          display: flex; gap: 8px; margin-top: 20px; flex-wrap: wrap; justify-content: center;
        }
        .mobile-lang-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.2);
          background: transparent; color: rgba(255,255,255,0.7);
          font-size: 13px; font-weight: 500; cursor: pointer; font-family: inherit;
          transition: all 0.2s;
        }
        .mobile-lang-btn:hover { border-color: var(--gold); color: white; }
        .mobile-lang-btn.active {
          background: rgba(201,168,76,0.2);
          border-color: var(--gold); color: var(--gold-light);
        }
        .mobile-lang-btn .flag { font-size: 16px; }
      `}</style>

      <nav className={`navbar ${scrolled ? "scrolled" : "transparent"}`}>
        <div className="navbar-logo" onClick={() => go("/")}>
          <img src={LOGO_SRC} alt="Anassik Travel" />
        </div>

        <div className="nav-links desktop">
          {nav.map(n => (
            <button
              key={n.path}
              className={`nav-link ${location.pathname === n.path ? "active" : ""}`}
              onClick={() => go(n.path)}
            >
              {n.label}
            </button>
          ))}
          <button className="nav-cta" onClick={() => go("/contact")}>{t("nav.bookNow")}</button>

          {/* Friendly language dropdown */}
          <div className="lang-dropdown-wrap">
            <button className="lang-trigger" onClick={() => setLangOpen(o => !o)}>
              <span className="flag">{currentLang.flag}</span>
              <span>{currentLang.label}</span>
              <span className={`chevron ${langOpen ? "open" : ""}`}>▼</span>
            </button>
            {langOpen && (
              <div className="lang-dropdown">
                {LANGS.map(l => (
                  <button
                    key={l.code}
                    className={`lang-option ${i18n.language === l.code ? "active" : ""}`}
                    onClick={() => changeLang(l.code)}
                  >
                    <span className="opt-flag">{l.flag}</span>
                    <span className="opt-label">{l.label}</span>
                    {i18n.language === l.code && <span className="opt-check">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </nav>

      {menuOpen && (
        <div className="mobile-menu">
          {nav.map(n => (
            <button key={n.path} className="mobile-nav-link" onClick={() => go(n.path)}>
              {n.label}
            </button>
          ))}
          <button className="btn-gold" style={{marginTop:12}} onClick={() => go("/contact")}>
            {t("nav.bookNow")}
          </button>
          <div className="mobile-lang-row">
            {LANGS.map(l => (
              <button
                key={l.code}
                className={`mobile-lang-btn ${i18n.language === l.code ? "active" : ""}`}
                onClick={() => { changeLang(l.code); setMenuOpen(false); }}
              >
                <span className="flag">{l.flag}</span>
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
